/**
 * Campaign Detail — run history & per-recipient logs.
 * Meta-style presentation, consistent with the WhatsApp Templates redesign:
 * subtle dot+text status indicators and muted text blocks for failure detail
 * instead of heavy chips/alerts.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, MenuItem, TextField,
  Alert, CircularProgress, Stack, Tooltip, TablePagination,
  Collapse, Chip,
} from '@mui/material';
import { ArrowBack, ExpandMore, ExpandLess, Replay, Refresh, ChevronRight, PersonOff, PlayArrow } from '@mui/icons-material';
import { getAuthHeaders } from '@/utils/api-headers.util';
import apiService from '@/services/api.service';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockFromErrorBody } from '@/utils/plan-lock.util';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CampaignSummary {
  _id: string;
  name: string;
  description?: string;
  status: string;
}

type RunTriggerEvent =
  | 'manual' | 'scheduled' | 'recurring' | 'newContact' | 'inactivity' | 'followUp' | 'workflow' | 'retry';
type RunStatus = 'in_progress' | 'completed' | 'partial' | 'failed';

interface CampaignRun {
  _id: string;
  triggerEvent: RunTriggerEvent;
  triggeredBy?: string;
  startedAt: string;
  completedAt?: string;
  status: RunStatus;
  failureReason?: string;
  totals: { targeted: number; sent: number; delivered: number; failed: number; skipped: number };
  /** Tier 3 retry chain: 0 = original run, 1..N = retry round; rootRunId groups the chain. */
  retryAttempt?: number;
  parentRunId?: string;
  rootRunId?: string;
  /** Set when the prepaid wallet stopped this run (backend CampaignRun.walletStop). */
  walletStop?: {
    stage: 'preflight' | 'midrun';
    at: string;
    unsent: number;
    message: string;
    resumable: boolean;
    resumedAt?: string;
    resumedRunId?: string;
  };
  /** This run was started by "Resume after top-up" from that wallet-stopped run. */
  resumedFromRunId?: string;
}

type LogStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'optOut' | 'skipped';

interface ProviderError {
  code?: number;
  subcode?: number;
  type?: string;
  title?: string;
  message?: string;
  details?: string;
  fbtraceId?: string;
  httpStatus?: number;
}

interface CampaignLog {
  _id: string;
  runId?: string;
  contactPhone: string;
  contactName?: string;
  triggerEvent: string;
  status: LogStatus;
  templateName?: string;
  templateLanguage?: string;
  mode?: 'text' | 'template';
  attempts?: number;
  error?: string;
  providerError?: ProviderError;
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Groups a page of runs into visual chains by `rootRunId` (an original run's
 * root is itself; each retry shares its parent's root — see
 * campaign.service.ts's getCampaignRuns). Used to draw a connector line
 * linking an original run to its retry rounds, so "Retry 1" reads as a
 * continuation of the run above it rather than an unrelated row.
 *
 * Rows stay in their existing startedAt-desc order — this only ANNOTATES each
 * row with its position in its (possibly single-row) chain; it never reorders
 * or removes rows. A chain that spans a page boundary (rare — retries fire
 * within hours of their parent, but not impossible on a busy campaign) simply
 * shows a partial connector on this page, which is honest: pagination is
 * server-side by startedAt, and stitching connectors across pages would need
 * a backend change out of scope for this pass.
 */
type ChainPosition = 'only' | 'first' | 'middle' | 'last';

function computeChainPositions(runs: CampaignRun[]): Map<string, ChainPosition> {
  const positions = new Map<string, ChainPosition>();
  const byRoot = new Map<string, CampaignRun[]>();
  for (const run of runs) {
    const root = run.rootRunId || run._id;
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root)!.push(run);
  }
  for (const members of byRoot.values()) {
    if (members.length === 1) {
      positions.set(members[0]!._id, 'only');
      continue;
    }
    // Chain members were pushed in the table's existing display order (newest
    // first), so the retry with the highest attempt number is index 0 (top of
    // the group) and the original run (retryAttempt 0) is the last index.
    members.forEach((m, i) => {
      positions.set(m._id, i === 0 ? 'first' : i === members.length - 1 ? 'last' : 'middle');
    });
  }
  return positions;
}

/**
 * The connector itself: a small vertical line + dot rendered to the left of a
 * chain member's "Started" cell. 'first'/'middle' draw the line downward to
 * the next row below; 'middle'/'last' draw it upward to continue the line
 * from the row above — together they form one unbroken line down the group,
 * with a filled dot marking each retry round and a hollow dot for the
 * original run at the bottom.
 */
function ChainConnector({ position }: { position: ChainPosition }): React.ReactElement | null {
  if (position === 'only') return null;
  const isOriginal = position === 'last';
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 20,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {/* Line above the dot — omitted for the first (topmost) member. */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: '50%',
          width: '2px',
          bgcolor: position === 'first' ? 'transparent' : 'warning.light',
        }}
      />
      {/* Line below the dot — omitted for the last (original run) member. */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          bottom: 0,
          width: '2px',
          bgcolor: position === 'last' ? 'transparent' : 'warning.light',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: isOriginal ? 'background.paper' : 'warning.main',
          border: '2px solid',
          borderColor: 'warning.main',
        }}
      />
    </Box>
  );
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function getRunStatusColor(status: RunStatus) {
  switch (status) {
    case 'completed': return 'success';
    case 'partial': return 'warning';
    case 'failed': return 'error';
    case 'in_progress': return 'info';
    default: return 'default';
  }
}

function formatRunStatusLabel(status: RunStatus): string {
  switch (status) {
    case 'in_progress': return 'In progress';
    case 'completed': return 'Completed';
    case 'partial': return 'Partial';
    case 'failed': return 'Failed';
    default: return status;
  }
}

function getLogStatusColor(status: LogStatus) {
  switch (status) {
    case 'delivered': return 'success';
    case 'sent': return 'info';
    case 'failed': return 'error';
    case 'optOut': return 'warning';
    case 'pending':
    case 'skipped':
    default: return 'default';
  }
}

function formatLogStatusLabel(status: LogStatus): string {
  switch (status) {
    case 'optOut': return 'Opted out';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  recurring: 'Recurring',
  newContact: 'New contact',
  inactivity: 'Inactivity',
  followUp: 'Follow-up',
  workflow: 'Workflow',
  retry: 'Retry',
};

function getTriggerLabel(triggerEvent: string): string {
  return TRIGGER_LABELS[triggerEvent] || triggerEvent;
}

/** Subtle dot + text status indicator, in the style of Meta Business Manager. */
function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: color === 'default' ? 'grey.400' : `${color}.main`,
        }}
      />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiBase = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
  const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
  const { guard } = usePlanGate();

  const [campaign, setCampaign] = useState<CampaignSummary | null>(null);
  // Drill-down navigation: 'runs' (Run History, default landing view) ->
  // 'runDetail' (Recipient Logs scoped to a single selected run).
  const [view, setView] = useState<'runs' | 'runDetail'>('runs');
  const [selectedRun, setSelectedRun] = useState<CampaignRun | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Run History
  const [runs, setRuns] = useState<CampaignRun[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(0); // 0-indexed for TablePagination
  const [runsLoading, setRunsLoading] = useState(false);
  const RUNS_LIMIT = 20;
  // Recomputed whenever the visible page of runs changes — see
  // computeChainPositions' doc comment for why this is page-scoped.
  const chainPositions = useMemo(() => computeChainPositions(runs), [runs]);

  // Recipient Logs (scoped to selectedRun)
  const [logs, setLogs] = useState<CampaignLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0); // 0-indexed
  const [logsLoading, setLogsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [errorCodeInput, setErrorCodeInput] = useState('');
  const [errorCodeFilter, setErrorCodeFilter] = useState('');
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Local-only annotation of a single-contact resend's outcome, keyed by the
  // original log's _id — the retry is recorded under a brand-new run (see
  // handleResend), so this run's own log row never changes; this is what
  // actually tells the admin whether the retry worked without leaving the page.
  const [retryResults, setRetryResults] = useState<Record<string, LogStatus>>({});
  const LOGS_LIMIT = 25;

  // Bulk retry (failed/skipped) of an entire run
  const [retryConfirm, setRetryConfirm] = useState<{ status: 'failed' | 'skipped'; count: number } | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [resuming, setResuming] = useState(false);

  // ── Load campaign summary ──────────────────────────────────────────────────

  useEffect(() => {
    // This read stays open even when campaignsEnabled is off (see campaign.routes.ts)
    // so real data still renders — only mutating actions are gated below.
    if (!id) return;
    (async () => {
      try {
        const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}`, { headers: authHdr() });
        const d = await r.json();
        if (d.success) setCampaign(d.data?.campaign || null);
        else setError(d.error?.message || 'Failed to load campaign');
      } catch (e: any) {
        setError(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Run History ─────────────────────────────────────────────────────────────

  const fetchRuns = useCallback(async () => {
    if (!id) return;
    setRunsLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('page', String(runsPage + 1));
      p.append('limit', String(RUNS_LIMIT));
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}/runs?${p}`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) {
        setRuns(d.data || []);
        setRunsTotal(d.meta?.total || 0);
      } else setError(d.error?.message || 'Failed to load run history');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRunsLoading(false);
    }
  }, [id, runsPage, apiBase]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // ── Recipient Logs (scoped to the selected run) ──────────────────────────────

  const fetchLogs = useCallback(async () => {
    if (!id || !selectedRun) return;
    setLogsLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('page', String(logsPage + 1));
      p.append('limit', String(LOGS_LIMIT));
      if (statusFilter !== 'all') p.append('status', statusFilter);
      if (contactSearch) p.append('contactPhone', contactSearch);
      // Only sent once it parses to a real integer — otherwise this behaves as
      // "no filter" rather than firing a request the backend would 400 on.
      if (errorCodeFilter && Number.isInteger(parseInt(errorCodeFilter, 10))) {
        p.append('errorCode', errorCodeFilter);
      }
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}/runs/${selectedRun._id}/logs?${p}`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) {
        setLogs(d.data || []);
        setLogsTotal(d.meta?.total || 0);
        setStatusCounts((d.meta?.statusCounts as Record<string, number>) || {});
      } else setError(d.error?.message || 'Failed to load recipient logs');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLogsLoading(false);
    }
  }, [id, selectedRun, logsPage, statusFilter, contactSearch, errorCodeFilter, apiBase]);

  useEffect(() => { if (view === 'runDetail') fetchLogs(); }, [view, fetchLogs]);

  // Reset to page 1 when filters change.
  useEffect(() => { setLogsPage(0); }, [statusFilter, contactSearch, errorCodeFilter]);

  // Debounce the contact-search box before it triggers a fetch — avoids
  // firing a request on every keystroke when scanning 1000+ recipients.
  useEffect(() => {
    const handle = setTimeout(() => setContactSearch(contactSearchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [contactSearchInput]);

  // Debounce the error-code box the same way.
  useEffect(() => {
    const handle = setTimeout(() => setErrorCodeFilter(errorCodeInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [errorCodeInput]);

  // ── Drill-down navigation ─────────────────────────────────────────────────────

  const handleViewRun = (run: CampaignRun) => {
    setSelectedRun(run);
    setView('runDetail');
    setLogsPage(0);
    setStatusFilter('all');
    setContactSearchInput('');
    setContactSearch('');
    setErrorCodeInput('');
    setErrorCodeFilter('');
    setExpandedLogId(null);
    setLogs([]);
    setStatusCounts({});
    setRetryResults({});
  };

  const handleBackToRuns = () => {
    setView('runs');
    setSelectedRun(null);
  };

  // ── Resend a failed message ─────────────────────────────────────────────────

  const handleResend = async (log: CampaignLog) => {
    if (!id) return;
    setResendingId(log._id);
    setError('');
    setSuccess('');
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}/trigger`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ contactPhone: log.contactPhone }),
      });
      const d = await r.json();
      if (d.success) {
        const result = d.data || {};
        setSuccess(`Resend to ${log.contactPhone}: ${result.sent ?? 0} sent, ${result.failed ?? 0} failed, ${result.skipped ?? 0} skipped`);
        // The resend is recorded under a brand-new CampaignRun, not this one
        // — this run's own log row for the contact never changes — so the
        // synchronous sent/failed/skipped counts above are the only signal
        // of what actually happened. Pin that outcome to the row directly.
        const outcome: LogStatus = result.sent ? 'sent' : result.failed ? 'failed' : 'skipped';
        setRetryResults((prev) => ({ ...prev, [log._id]: outcome }));
      } else if (!showPlanLockFromErrorBody(d.error)) setError(d.error?.message || 'Failed to resend');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setResendingId(null);
    }
  };

  // ── Resume a run the prepaid wallet stopped (after a top-up) ────────────────
  // The backend claims the stop atomically, re-sends only contacts the wallet
  // kept from sending, and answers 402 (wallet dialog) if it still can't cover it.

  const handleResumeWallet = async (run: CampaignRun) => {
    if (!id) return;
    setResuming(true);
    setError('');
    setSuccess('');
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}/runs/${run._id}/resume`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const d = await r.json();
      if (d.success) {
        setSuccess(d.data?.message || `Resumed for ${d.data?.total ?? 0} contact(s)…`);
        handleBackToRuns();
        fetchRuns();
      } else if (!showPlanLockFromErrorBody(d.error)) {
        setError(d.error?.message || 'Could not resume this run');
        if (r.status === 409) fetchRuns();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setResuming(false);
    }
  };

  // ── Bulk retry of an entire run's failed/skipped contacts ───────────────────

  const handleConfirmBulkRetry = async () => {
    if (!id || !selectedRun || !retryConfirm) return;
    setRetrying(true);
    setError('');
    setSuccess('');
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${id}/runs/${selectedRun._id}/retry`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ statuses: [retryConfirm.status] }),
      });
      const d = await r.json();
      if (d.success) {
        const result = d.data || {};
        setSuccess(result.message || `Retrying ${result.total ?? 0} contact(s)…`);
        handleBackToRuns();
        fetchRuns();
      } else if (!showPlanLockFromErrorBody(d.error)) setError(d.error?.message || 'Failed to start retry');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRetrying(false);
      setRetryConfirm(null);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate('/admin/campaigns')} size="small">
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">{campaign?.name || 'Campaign'}</Typography>
            {campaign?.description && (
              <Typography variant="body2" color="text.secondary">{campaign.description}</Typography>
            )}
          </Box>
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Contacts this campaign has never delivered a message to — useful for re-targeting or list cleanup">
            <Button
              size="small"
              variant="outlined"
              startIcon={<PersonOff fontSize="small" />}
              onClick={() => navigate(`/admin/contacts?advanced=neverDeliveredThisCampaign&campaignId=${id}`)}
            >
              Never-delivered contacts
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => {
                if (view === 'runs') fetchRuns();
                if (view === 'runDetail') fetchLogs();
              }}
              disabled={view === 'runs' ? runsLoading : logsLoading}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* ── Run History (default landing view) ─────────────────────────── */}
          {view === 'runs' && (
            runsLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : runs.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Typography color="text.secondary">No runs yet.</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Started</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Targeted</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Delivered</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Skipped</TableCell>
                        <TableCell>Completed</TableCell>
                        <TableCell align="right">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runs.map((run) => {
                        const chainPosition = chainPositions.get(run._id) ?? 'only';
                        return (
                        <React.Fragment key={run._id}>
                          <TableRow hover onClick={() => handleViewRun(run)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={{ position: 'relative' }}>
                              {/* A run linked to retries (or itself a retry) gets a connector
                                  line threading it to the rest of its chain — see
                                  ChainConnector/computeChainPositions above. */}
                              <ChainConnector position={chainPosition} />
                              <Box sx={{ pl: chainPosition === 'only' ? 0 : 3 }}>
                                {formatDateTime(run.startedAt)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {run.triggerEvent === 'retry' ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={`Retry ${run.retryAttempt ?? ''}`.trim()}
                                />
                              ) : (
                                getTriggerLabel(run.triggerEvent)
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <StatusDot color={getRunStatusColor(run.status)} label={formatRunStatusLabel(run.status)} />
                              {run.walletStop && (
                                <Chip
                                  size="small"
                                  color={run.walletStop.resumedAt ? 'default' : 'warning'}
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                  label={run.walletStop.resumedAt ? 'Wallet stop · resumed' : 'Stopped — wallet'}
                                />
                              )}
                              {run.resumedFromRunId && (
                                <Chip size="small" variant="outlined" sx={{ ml: 1 }} label="Resumed after top-up" />
                              )}
                              {run.failureReason && (
                                <Box sx={{ mt: 0.75, pl: 1, borderLeft: '2px solid', borderColor: 'error.main' }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      display: 'block',
                                      lineHeight: 1.3,
                                      fontSize: '0.7rem',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {run.failureReason}
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="right">{run.totals.targeted}</TableCell>
                            <TableCell align="right">{run.totals.sent}</TableCell>
                            <TableCell align="right">{run.totals.delivered}</TableCell>
                            <TableCell align="right">{run.totals.failed}</TableCell>
                            <TableCell align="right">{run.totals.skipped}</TableCell>
                            <TableCell>{formatDateTime(run.completedAt)}</TableCell>
                            <TableCell align="right">
                              <ChevronRight color="action" fontSize="small" />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={runsTotal}
                  page={runsPage}
                  onPageChange={(_e, p) => setRunsPage(p)}
                  rowsPerPage={RUNS_LIMIT}
                  rowsPerPageOptions={[RUNS_LIMIT]}
                />
              </>
            )
          )}

          {/* ── Recipient Logs (drilled into a single run) ──────────────────── */}
          {view === 'runDetail' && selectedRun && (
            <Box>
              {/* Breadcrumb + run summary header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Button size="small" startIcon={<ArrowBack fontSize="small" />} onClick={handleBackToRuns} sx={{ mb: 1 }}>
                  Back to Run History
                </Button>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatDateTime(selectedRun.startedAt)} · {getTriggerLabel(selectedRun.triggerEvent)}
                  </Typography>
                  <StatusDot color={getRunStatusColor(selectedRun.status)} label={formatRunStatusLabel(selectedRun.status)} />
                </Stack>

                {selectedRun.status !== 'in_progress' &&
                  (selectedRun.totals.failed > 0 || selectedRun.totals.skipped > 0) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                      {selectedRun.totals.failed > 0 && (
                        <Button
                          size="small" variant="outlined" color="error" startIcon={<Replay fontSize="small" />}
                          onClick={guard('campaignsEnabled', () => setRetryConfirm({ status: 'failed', count: selectedRun.totals.failed }))}
                        >
                          Retry failed ({selectedRun.totals.failed})
                        </Button>
                      )}
                      {selectedRun.totals.skipped > 0 && !(selectedRun.walletStop && !selectedRun.walletStop.resumedAt) && (
                        <Button
                          size="small" variant="outlined" startIcon={<Replay fontSize="small" />}
                          onClick={guard('campaignsEnabled', () => setRetryConfirm({ status: 'skipped', count: selectedRun.totals.skipped }))}
                        >
                          Retry skipped ({selectedRun.totals.skipped})
                        </Button>
                      )}
                    </Stack>
                  )}

                {selectedRun.walletStop ? (
                  <Alert
                    severity={selectedRun.walletStop.resumedAt ? 'info' : 'warning'}
                    sx={{ mt: 1.5 }}
                    action={
                      selectedRun.walletStop.resumable && !selectedRun.walletStop.resumedAt ? (
                        <Stack direction="row" spacing={1}>
                          <Button color="inherit" size="small" onClick={() => navigate('/admin/billing/wallet')}>
                            Top up
                          </Button>
                          <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            startIcon={resuming ? <CircularProgress size={14} color="inherit" /> : <PlayArrow fontSize="small" />}
                            disabled={resuming}
                            onClick={guard('campaignsEnabled', () => handleResumeWallet(selectedRun))}
                          >
                            Resume
                          </Button>
                        </Stack>
                      ) : undefined
                    }
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {selectedRun.walletStop.stage === 'preflight'
                        ? 'Not sent — the wallet could not cover this run'
                        : 'Stopped part-way — the wallet ran out'}
                    </Typography>
                    <Typography variant="body2">{selectedRun.walletStop.message}</Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {selectedRun.walletStop.resumedAt
                        ? `Resumed on ${formatDateTime(selectedRun.walletStop.resumedAt)} — see the newer run in the history.`
                        : selectedRun.walletStop.resumable
                          ? `${selectedRun.walletStop.unsent} contact(s) not sent. Top up, then Resume — only they will get the message.`
                          : "This campaign's next automatic sweep sends to these contacts once the wallet is topped up."}
                    </Typography>
                  </Alert>
                ) : selectedRun.failureReason && (
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{selectedRun.failureReason}</Typography>
                  </Box>
                )}
              </Box>

              <Stack direction="row" spacing={2} sx={{ p: 2 }} flexWrap="wrap">
                <TextField
                  size="small" select label="Status" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)} sx={{ width: 160 }}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="optOut">Opted out</MenuItem>
                  <MenuItem value="skipped">Skipped</MenuItem>
                </TextField>
                <TextField
                  size="small" label="Search by phone number" placeholder="e.g. 9198…"
                  value={contactSearchInput}
                  onChange={(e) => setContactSearchInput(e.target.value)}
                  sx={{ width: 220 }}
                />
                <TextField
                  size="small" label="Error code" placeholder="e.g. 131026"
                  value={errorCodeInput}
                  onChange={(e) => setErrorCodeInput(e.target.value.replace(/[^0-9]/g, ''))}
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{ width: 160 }}
                />
              </Stack>

              {/* Summary counts across the full filtered set (not just this page) */}
              {logsTotal > 0 && (
                <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }} flexWrap="wrap">
                  <Chip size="small" label={`Total: ${logsTotal}`} />
                  <Chip
                    size="small" color="success" variant="outlined"
                    label={`Delivered: ${statusCounts['delivered'] || 0}`}
                  />
                  <Chip
                    size="small" color="warning" variant="outlined"
                    label={`Not delivered: ${logsTotal - (statusCounts['delivered'] || 0)}`}
                  />
                  {(statusCounts['failed'] || 0) > 0 && (
                    <Chip size="small" color="error" variant="outlined" label={`Failed: ${statusCounts['failed']}`} />
                  )}
                  {(statusCounts['pending'] || 0) > 0 && (
                    <Chip size="small" variant="outlined" label={`Pending: ${statusCounts['pending']}`} />
                  )}
                  {(statusCounts['sent'] || 0) > 0 && (
                    <Chip size="small" color="info" variant="outlined" label={`Sent: ${statusCounts['sent']}`} />
                  )}
                  {(statusCounts['skipped'] || 0) > 0 && (
                    <Chip size="small" variant="outlined" label={`Skipped: ${statusCounts['skipped']}`} />
                  )}
                  {(statusCounts['optOut'] || 0) > 0 && (
                    <Chip size="small" color="warning" variant="outlined" label={`Opted out: ${statusCounts['optOut']}`} />
                  )}
                </Stack>
              )}

              {logsLoading ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : logs.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <Typography color="text.secondary">No recipient logs found.</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>Contact</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Error summary</TableCell>
                          <TableCell>Sent</TableCell>
                          <TableCell>Delivered</TableCell>
                          <TableCell>Failed</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log) => {
                          const expanded = expandedLogId === log._id;
                          const hasDetail = Boolean(log.providerError || log.error);
                          const retryResult = retryResults[log._id];
                          return (
                            <React.Fragment key={log._id}>
                              <TableRow hover>
                                <TableCell sx={{ width: 40 }}>
                                  {hasDetail && (
                                    <IconButton size="small" onClick={() => setExpandedLogId(expanded ? null : log._id)}>
                                      {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                    </IconButton>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{log.contactPhone}</Typography>
                                  {log.contactName && (
                                    <Typography variant="caption" color="text.secondary">{log.contactName}</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <StatusDot color={getLogStatusColor(log.status)} label={formatLogStatusLabel(log.status)} />
                                  {retryResult && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      ↻ Retried → {formatLogStatusLabel(retryResult)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                                    {log.error || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDateTime(log.sentAt)}</TableCell>
                                <TableCell>{formatDateTime(log.deliveredAt)}</TableCell>
                                <TableCell>{formatDateTime(log.failedAt)}</TableCell>
                                <TableCell align="right">
                                  {log.status === 'failed' && (
                                    <Tooltip title="Resend to this contact">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={resendingId === log._id}
                                          onClick={guard('campaignsEnabled', () => handleResend(log))}
                                        >
                                          {resendingId === log._id ? <CircularProgress size={16} /> : <Replay fontSize="small" />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                              {hasDetail && (
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ py: 0, borderTop: 'none' }}>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, my: 1 }}>
                                        <Stack spacing={0.5}>
                                          {log.mode && (
                                            <Typography variant="caption" color="text.secondary">
                                              Mode: {log.mode}
                                              {log.templateName ? ` · Template: ${log.templateName} (${log.templateLanguage})` : ''}
                                              {log.attempts && log.attempts > 1 ? ` · Attempts: ${log.attempts}` : ''}
                                            </Typography>
                                          )}
                                          {log.providerError ? (
                                            <>
                                              {log.providerError.message && (
                                                <Typography variant="body2">{log.providerError.message}</Typography>
                                              )}
                                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {log.providerError.code !== undefined && (
                                                  <Chip size="small" variant="outlined" label={`code: ${log.providerError.code}`} />
                                                )}
                                                {log.providerError.subcode !== undefined && (
                                                  <Chip size="small" variant="outlined" label={`subcode: ${log.providerError.subcode}`} />
                                                )}
                                                {log.providerError.type && (
                                                  <Chip size="small" variant="outlined" label={`type: ${log.providerError.type}`} />
                                                )}
                                                {log.providerError.httpStatus !== undefined && (
                                                  <Chip size="small" variant="outlined" label={`http: ${log.providerError.httpStatus}`} />
                                                )}
                                              </Stack>
                                              {log.providerError.details && (
                                                <Typography variant="caption" color="text.secondary">
                                                  {log.providerError.details}
                                                </Typography>
                                              )}
                                              {log.providerError.fbtraceId && (
                                                <Typography variant="caption" color="text.secondary">
                                                  fbtrace_id: {log.providerError.fbtraceId}
                                                </Typography>
                                              )}
                                            </>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">{log.error}</Typography>
                                          )}
                                        </Stack>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={logsTotal}
                    page={logsPage}
                    onPageChange={(_e, p) => setLogsPage(p)}
                    rowsPerPage={LOGS_LIMIT}
                    rowsPerPageOptions={[LOGS_LIMIT]}
                  />
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Convenience: open in new tab via direct link from list ─────────── */}
      <Box mt={2}>
        <Button size="small" onClick={() => navigate(`/admin/campaigns/${id}/edit`)}>Edit campaign</Button>
      </Box>

      <ConfirmDialog
        open={!!retryConfirm}
        title={retryConfirm?.status === 'failed' ? 'Retry Failed Recipients' : 'Retry Skipped Recipients'}
        content={
          <>
            This will resend the message to {retryConfirm?.count} contact{retryConfirm?.count === 1 ? '' : 's'} who
            were marked &quot;{retryConfirm?.status}&quot; in this run, as a new broadcast run. Are you sure?
          </>
        }
        confirmText="Retry"
        severity="warning"
        loading={retrying}
        onConfirm={handleConfirmBulkRetry}
        onCancel={() => setRetryConfirm(null)}
      />
    </Box>
  );
};

export default CampaignDetail;
